import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { Badge, Collapse, Typography } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import EventNoteOutlinedIcon from "@material-ui/icons/EventNoteOutlined";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import ExtensionIcon from "@material-ui/icons/Extension";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import useTickets from "../hooks/useTickets";
import openSocket from "../services/socket-io";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0.75, 1, 1.5),
    background: theme.custom.sidebar,
    color: theme.custom.tokens.sidebarTextActive
  },
  rootCollapsed: {
    padding: theme.spacing(1.25, 0.75, 2)
  },
  // Rotulo de grupo = "overline" do design system (Medium, caixa alta).
  groupLabel: {
    margin: theme.spacing(1.5, 0.25, 0.5),
    fontSize: "0.625rem",
    fontWeight: 500,
    letterSpacing: "0.08em",
    color: theme.custom.tokens.sidebarText,
    opacity: 0.75,
    textTransform: "uppercase",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(2, 0.25, 0.75),
      fontSize: "0.6875rem"
    }
  },
  /*
   * Densidade da navegacao.
   * =============================================================
   * Os valores anteriores (44px + 8px de margem por item) somavam
   * ~790px de menu — mais alto que a area util de um notebook de
   * 768px, e por isso a sidebar aparecia com barra de rolagem.
   *
   * No desktop o item fica compacto; abaixo de "sm" ele volta a 44px,
   * que e o alvo minimo confortavel para o dedo no celular.
   * =============================================================
   */
  item: {
    minHeight: 36,
    margin: theme.spacing(0.25, 0),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.custom.radius.md,
    color: theme.custom.tokens.sidebarText,
    borderLeft: "3px solid transparent",
    overflow: "hidden",
    transition: `background-color ${theme.custom.motion.fast}, color ${theme.custom.motion.fast}`,
    "&:hover": {
      background: "rgba(255, 255, 255, 0.08)",
      color: theme.custom.tokens.sidebarTextActive
    },
    "& .MuiListItemIcon-root": {
      minWidth: 32,
      color: "inherit",
      opacity: 1
    },
    "& .MuiListItemIcon-root svg, & .MuiListItemIcon-root .MuiSvgIcon-root, & svg.MuiSvgIcon-root": {
      color: "inherit",
      fill: "currentColor",
      fontSize: 18
    },
    "& .MuiBadge-root svg, & .MuiBadge-root .MuiSvgIcon-root": {
      color: "inherit",
      fill: "currentColor"
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.8125rem",
      fontWeight: 400,
      letterSpacing: "0.01em",
      color: "inherit",
      opacity: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    // No celular a navegacao e tocada, nao clicada: alvo maior.
    [theme.breakpoints.down("sm")]: {
      minHeight: 44,
      padding: theme.spacing(0.75, 1.25),
      "& .MuiListItemIcon-root": { minWidth: 40 },
      "& .MuiListItemIcon-root svg, & svg.MuiSvgIcon-root": { fontSize: 20 },
      "& .MuiListItemText-primary": { fontSize: "0.875rem" }
    }
  },
  itemCollapsed: {
    justifyContent: "center",
    padding: theme.spacing(0.75),
    "& .MuiListItemIcon-root": {
      minWidth: 0,
      justifyContent: "center"
    }
  },
  itemTwoLine: {
    minHeight: 44,
    "& .MuiListItemText-primary": {
      whiteSpace: "normal !important",
      overflow: "visible",
      textOverflow: "clip",
      lineHeight: 1.3,
      fontSize: "0.8125rem"
    }
  },
  twoLineLabel: {
    display: "block",
    lineHeight: 1.3
  },
  // Item ativo: fundo de acento + barra lateral, texto em Medium.
  activeItem: {
    background: theme.custom.tokens.sidebarActiveBg,
    color: theme.custom.tokens.sidebarTextActive,
    borderLeftColor: theme.custom.tokens.accent,
    "& .MuiListItemIcon-root, & .MuiSvgIcon-root": {
      color: theme.custom.tokens.sidebarTextActive
    },
    "& .MuiListItemText-primary": {
      color: theme.custom.tokens.sidebarTextActive,
      fontWeight: 500
    }
  },
  submenu: {
    marginLeft: theme.spacing(0.75),
    paddingLeft: theme.spacing(0.75),
    borderLeft: "1px solid rgba(148, 163, 184, 0.14)"
  },
  submenuCollapsed: {
    marginLeft: 0,
    paddingLeft: 0,
    borderLeft: 0
  },
  sectionButton: {
    marginTop: theme.spacing(0.5)
  }
}));

function ListItemLink(props) {
  const { icon, primary, to, className, activeClassName, collapsed, collapsedClassName, badgeContent = 0 } = props;
  const location = useLocation();
  const active = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
  const showBadge = Number(badgeContent) > 0;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem
        button
        component={renderLink}
        title={collapsed ? primary : undefined}
        className={`${className || ""} ${collapsed ? collapsedClassName || "" : ""} ${active ? activeClassName || "" : ""}`}
      >
        {/*
          O icone herda a cor do item (mutada quando inativo, branca
          quando ativo) em vez de ser fixado em branco — e isso que da
          a hierarquia visual da navegacao.
        */}
        {icon ? (
          <ListItemIcon>
            {showBadge ? (
              <Badge badgeContent={badgeContent} color="secondary">
                {inheritIcon(icon)}
              </Badge>
            ) : (
              inheritIcon(icon)
            )}
          </ListItemIcon>
        ) : null}
        {!collapsed && <ListItemText primary={primary} />}
      </ListItem>
    </li>
  );
}

/* Icone que segue a cor do item pai (ativo x inativo). */
const inheritIcon = icon =>
  React.cloneElement(icon, {
    style: { ...(icon.props?.style || {}), color: "inherit", fill: "currentColor" },
    htmlColor: "inherit"
  });

/* Alias mantido: pontos do menu que ainda chamam whiteIcon(). */
const whiteIcon = inheritIcon;

const MainListItems = (props) => {
  const { drawerClose, drawerOpen = true } = props;
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [adminOpen, setAdminOpen] = useState(true);
  const { tickets: unreadTickets } = useTickets({ withUnreadMessages: "true" });
  const [unreadTicketIds, setUnreadTicketIds] = useState(new Set());
  const ticketsBadgeCount = unreadTicketIds.size;
  const isAdmin = user?.profile === "admin";
  const hasPermission = permission => user?.permissions?.[permission] === true;
  const hasSettingsPermission = [
    "settings.view",
    "settings.manage",
    "settings.logo",
    "settings.categories",
    "settings.categories.view",
    "settings.categories.create",
    "settings.categories.edit",
    "settings.categories.delete",
    "settings.closing_reasons",
    "settings.closing_reasons.view",
    "settings.closing_reasons.create",
    "settings.closing_reasons.edit",
    "settings.closing_reasons.delete",
    "settings.satisfaction",
    "settings.satisfaction.view",
    "settings.satisfaction.create",
    "settings.satisfaction.edit",
    "settings.satisfaction.delete",
    "settings.audit_logs",
    "settings.ura",
    "settings.ura_flows",
    "settings.ura_options",
    "settings.forms",
    "settings.form_builder",
    "settings.form_responses",
    "settings.form_reports",
    "settings.groups",
    "tags.view",
    "tags.create",
    "tags.edit",
    "tags.delete"
  ].some(hasPermission);
  /*
   * O perfil governa o menu.
   * =============================================================
   * Antes cada item tinha atalhos por tipo de conta
   * (`|| isSupervisor`, `|| user?.profile === "user"`), entao um
   * supervisor via Configuracoes mesmo com o perfil sem nenhuma tela
   * dentro, e todo atendente via Tickets/Contatos/Respostas
   * independente do que o perfil concedia. O perfil virava decorativo.
   *
   * Agora so o Administrador tem acesso irrestrito; todo o resto sai
   * exclusivamente do perfil configurado.
   * =============================================================
   */
  const canSeeMural = isAdmin || hasPermission("mural.view");
  const canSeeTickets = isAdmin || hasPermission("tickets.view");
  const canSeeContacts = isAdmin || hasPermission("contacts.view");
  const canSeeDashboard = isAdmin || hasPermission("dashboard.view");
  const canSeeReports = isAdmin || hasPermission("reports.view");
  const canSeeQuickAnswers = isAdmin || hasPermission("quickAnswers.view");
  const canSeeSchedules = isAdmin || hasPermission("scheduledMessages.view") || hasPermission("campaigns.view");
  const canSeeConnections = isAdmin || hasPermission("connections.view");
  const canSeeUsers = isAdmin || hasPermission("users.view");
  const canSeeQueues = isAdmin || hasPermission("queues.view");
  // Configuracoes so aparece se houver ao menos UMA tela dentro dela.
  const canSeeSettings = isAdmin || hasSettingsPermission;
  const canSeeIntegrations = isAdmin || hasPermission("integrations.view") || hasPermission("glpi.view") || hasPermission("whatsapp_provider.view") || hasPermission("knowledge.manage");
  const canSeeProfiles = isAdmin || hasPermission("profiles.manage");
  const canSeeAdminSection = canSeeSettings || canSeeConnections || canSeeUsers || canSeeQueues || canSeeIntegrations || canSeeProfiles;

  /*
   * Titulo de secao so aparece se houver algum item dentro dela.
   *
   * Antes o titulo era renderizado direto, gerando "GESTAO" e "AUTOMACAO"
   * vazios para quem nao tem essas permissoes: o menu anunciava areas que a
   * pessoa nao pode abrir, o que parece defeito e ainda conta o que existe no
   * sistema para quem nao deveria saber.
   */
  const temSecaoAtendimento = canSeeMural || canSeeTickets || canSeeContacts;
  const temSecaoGestao = canSeeDashboard || canSeeReports;
  const temSecaoAutomacao = canSeeQuickAnswers || canSeeSchedules;

  useEffect(() => {
    setUnreadTicketIds(new Set(unreadTickets.map(ticket => Number(ticket.id))));
  }, [unreadTickets]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinNotification"));

    socket.on("appMessage", data => {
      if (
        data.action === "create" &&
        !data.message.read &&
        (data.ticket.userId === user?.id || !data.ticket.userId)
      ) {
        setUnreadTicketIds(prevIds => {
          const nextIds = new Set(prevIds);
          nextIds.add(Number(data.ticket.id));
          return nextIds;
        });
      }
    });

    socket.on("ticket", data => {
      if (data.action === "updateUnread" || data.action === "delete") {
        setUnreadTicketIds(prevIds => {
          const nextIds = new Set(prevIds);
          nextIds.delete(Number(data.ticketId));
          return nextIds;
        });
      }
    });

    return () => socket.disconnect();
  }, [user?.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          // Mesma razao do sino (ver NotificationsPopOver): conexao
          // desligada de proposito fica em DISCONNECTED e nao e problema
          // a resolver. `!== false` mantem o aviso quando o campo nao
          // vier carregado.
          if (whats.active === false) return false;

          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose} className={`${classes.root} ${!drawerOpen ? classes.rootCollapsed : ""}`}>
      {drawerOpen && temSecaoAtendimento && (
        <Typography className={classes.groupLabel}>ATENDIMENTO</Typography>
      )}
      {canSeeMural && (
        <ListItemLink
          to="/"
          primary="Central de Avisos"
          icon={<AssignmentOutlinedIcon />}
          className={classes.item}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
        />
      )}
      {canSeeTickets && (
        <ListItemLink
          to="/tickets"
          primary="Atendimentos"
          icon={<WhatsAppIcon />}
          className={classes.item}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
          badgeContent={ticketsBadgeCount}
        />
      )}
      {canSeeContacts && (
        <ListItemLink
          to="/contacts"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          icon={<ContactPhoneOutlinedIcon />}
          className={classes.item}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
        />
      )}

      {drawerOpen && temSecaoGestao && (
        <Typography className={classes.groupLabel}>GESTAO</Typography>
      )}
      {canSeeDashboard && (
        <ListItemLink
          to="/painel"
          primary="Painel de gestão"
          icon={<DashboardOutlinedIcon />}
          className={classes.item}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
        />
      )}
      {canSeeReports && (
        <ListItemLink
          to="/reports"
          primary="Relatórios"
          icon={<AssessmentOutlinedIcon />}
          className={classes.item}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
        />
      )}

      {drawerOpen && temSecaoAutomacao && (
        <Typography className={classes.groupLabel}>AUTOMACAO</Typography>
      )}
      {canSeeQuickAnswers && (
        <ListItemLink
          to="/quickAnswers"
          primary="Respostas rapidas"
          icon={<QuestionAnswerOutlinedIcon />}
          className={classes.item}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
        />
      )}
      {canSeeSchedules && (
        <ListItemLink
          to="/campaigns-schedules"
          primary={<span className={classes.twoLineLabel}>Mensagens<br />programadas</span>}
          icon={<EventNoteOutlinedIcon />}
          className={`${classes.item} ${classes.itemTwoLine}`}
          activeClassName={classes.activeItem}
          collapsed={!drawerOpen}
          collapsedClassName={classes.itemCollapsed}
        />
      )}
      {canSeeAdminSection && (
          <>
            {drawerOpen && <Typography className={classes.groupLabel}>CONFIGURACOES</Typography>}
            <ListItem
              button
              title={!drawerOpen ? i18n.t("mainDrawer.listItems.administration") : undefined}
              className={`${classes.item} ${classes.sectionButton} ${!drawerOpen ? classes.itemCollapsed : ""}`}
              onClick={event => {
                event.stopPropagation();
                setAdminOpen(prev => !prev);
              }}
            >
              <ListItemIcon>
                {whiteIcon(<SecurityOutlinedIcon />)}
              </ListItemIcon>
              {drawerOpen && <ListItemText primary={i18n.t("mainDrawer.listItems.administration")} />}
              {drawerOpen && (adminOpen ? whiteIcon(<ExpandLess />) : whiteIcon(<ExpandMore />))}
            </ListItem>
            <Collapse
              in={adminOpen}
              timeout="auto"
              unmountOnExit
              className={`${classes.submenu} ${!drawerOpen ? classes.submenuCollapsed : ""}`}
            >
              {(canSeeUsers || canSeeConnections) && (
                <>
                  {canSeeConnections && (
                    <ListItemLink
                      to="/connections"
                      primary={i18n.t("mainDrawer.listItems.connections")}
                      icon={
                        <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                          <SyncAltIcon />
                        </Badge>
                      }
                      className={classes.item}
                      activeClassName={classes.activeItem}
                      collapsed={!drawerOpen}
                      collapsedClassName={classes.itemCollapsed}
                    />
                  )}
                  {canSeeUsers && (
                    <ListItemLink
                      to="/users"
                      primary={i18n.t("mainDrawer.listItems.users")}
                      icon={<PeopleAltOutlinedIcon />}
                      className={classes.item}
                      activeClassName={classes.activeItem}
                      collapsed={!drawerOpen}
                      collapsedClassName={classes.itemCollapsed}
                    />
                  )}
                </>
              )}
              {canSeeQueues && (
                <ListItemLink
                  to="/queues"
                  primary={i18n.t("mainDrawer.listItems.queues")}
                  icon={<AccountTreeOutlinedIcon />}
                  className={classes.item}
                  activeClassName={classes.activeItem}
                  collapsed={!drawerOpen}
                  collapsedClassName={classes.itemCollapsed}
                />
              )}
              {canSeeProfiles && (
                <ListItemLink
                  to="/profiles"
                  primary="Perfis"
                  icon={<AssignmentIndOutlinedIcon />}
                  className={classes.item}
                  activeClassName={classes.activeItem}
                  collapsed={!drawerOpen}
                  collapsedClassName={classes.itemCollapsed}
                />
              )}
              {canSeeSettings && (
                <ListItemLink
                  to="/settings"
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  icon={<SettingsOutlinedIcon />}
                  className={classes.item}
                  activeClassName={classes.activeItem}
                  collapsed={!drawerOpen}
                  collapsedClassName={classes.itemCollapsed}
                />
              )}
              {canSeeIntegrations && (
                <ListItemLink
                  to="/integrations"
                  primary="Integrações"
                  icon={<ExtensionIcon />}
                  className={classes.item}
                  activeClassName={classes.activeItem}
                  collapsed={!drawerOpen}
                  collapsedClassName={classes.itemCollapsed}
                />
              )}
            </Collapse>
          </>
      )}
    </div>
  );
};

export default MainListItems;

